import { EHeadingStyleVariant, Heading, SkeletonLine } from '@outdoorsyco/bonfire';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRecentlyViewedRentalTiles } from '@/hooks/useRecentlyViewedRentalTiles';
import { useGetWishlists } from '@/hooks/useWishlists';
import { getIsAuthenticated } from '@/redux/selectors/auth/user';
import { EListingSource } from '@/services/analytics/listings/types';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { canReadWishlist, EWishlistRoles } from '@/utility/wishlists';

import { EmptyWishlistMessage } from './EmptyWishlistMessage';
import { InspiringWishlists } from './InspiringWishlists';
import { WishlistGrid } from './WishlistGrid';
import { WishlistsActions } from './WishlistsActions';
import { WishlistsAuthPrompt } from './WishlistsAuthPrompt';
import { useWishlistsWelcomeModal, WishlistsWelcomeModal } from './WishlistsWelcomeModal';

const VIDEO_SRC = '/assets/wishlists/main-page/wishlist.mp4';
const IMAGE_FALLBACK_SRC = '/assets/wishlists/main-page/wishlist-fallback.png';
const WELCOME_MODAL_KEY = 'wishlist_welcome_modal_seen';

const WishlistsSkeleton = () => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
    {/* Recently viewed card skeleton */}
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-center aspect-square rounded-2xl bg-[#F1F2F3]">
        <SkeletonLine className="w-12 h-12 rounded-full !bg-gray-200" />
      </div>
      <div className="flex flex-col">
        <SkeletonLine className="w-3/4 h-5 !bg-gray-200" />
        <SkeletonLine className="w-1/2 h-4 !bg-gray-200" />
      </div>
    </div>
    {/* Wishlist cards skeleton */}
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="flex flex-col gap-2">
        <div className="flex items-center justify-center aspect-square rounded-2xl bg-[#F1F2F3]">
          <SkeletonLine className="w-12 h-12 rounded-full !bg-gray-200" />
        </div>
        <div className="flex flex-col">
          <SkeletonLine className="w-3/4 h-5 !bg-gray-200" />
          <SkeletonLine className="w-1/2 h-4 !bg-gray-200" />
        </div>
      </div>
    ))}
  </div>
);

export const WishlistsTemplate = () => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();
  const isAuthenticated = useSelector(getIsAuthenticated);

  const rentalCategory = ERentalCategory.RV;

  const {
    data: wishlists,
    mutate: refetchWishlists,
    error,
    isValidating: isWishlistsLoading,
  } = useGetWishlists({ isAuthenticated, rentalCategory });
  const { recentlyViewedTiles, isLoading: recentlyViewedTilesLoading } =
    useRecentlyViewedRentalTiles(EListingSource.WISHLIST);

  const { isModalOpen, closeModal } = useWishlistsWelcomeModal({ modalKey: WELCOME_MODAL_KEY });

  const [editMode, setEditMode] = useState(false);

  // Split wishlists into regular and inspiring
  const regularWishlists = wishlists
    ? wishlists.filter(wishlist => !wishlist.is_example && canReadWishlist(wishlist?.user_role))
    : [];
  const inspiringWishlists = wishlists ? wishlists.filter(wishlist => wishlist.is_example) : [];

  const hasRegularWishlists = regularWishlists.length > 0;
  const hasInspiringWishlists = inspiringWishlists.length > 0;
  const hasRecentlyViewedTiles =
    recentlyViewedTiles &&
    recentlyViewedTiles.length > 0 &&
    recentlyViewedTiles?.filter(tile => tile.viewedUpdated).length > 0;

  // Update the loading logic to properly check if data is still loading
  const isLoading = (!wishlists && !error && isWishlistsLoading) || recentlyViewedTilesLoading;

  const showInspiringWishlists =
    !isLoading &&
    !hasRecentlyViewedTiles &&
    !hasRegularWishlists &&
    hasInspiringWishlists &&
    !isMobile;
  const showWishlistGrid =
    !isLoading && (isMobile || hasRegularWishlists || hasRecentlyViewedTiles);

  const showEmptyWishlistMessage =
    !isLoading && !(isMobile || hasRegularWishlists || hasRecentlyViewedTiles);

  const exploreRentalsBtnLabel = intl.formatMessage({
    defaultMessage: 'Explore rentals',
    id: 'tfimpO',
  });

  const welcomeModalHeaderText = isMobile
    ? intl.formatMessage({
        defaultMessage: 'Save your favorites',
        id: '62pTkz',
      })
    : intl.formatMessage({
        defaultMessage: 'Welcome to wishlists',
        id: 'Qsl2fG',
      });
  const welcomeModalContentTitle = isMobile
    ? intl.formatMessage({
        defaultMessage: 'Save your favorites',
        id: '62pTkz',
      })
    : '';
  const welcomeModalContentText = intl.formatMessage({
    defaultMessage: 'Click the heart icon as you browse to save your favorites.',
    id: '+NmvWX',
  });
  const welcomeModalButtonLabel = intl.formatMessage({
    defaultMessage: 'Got it',
    id: 'NYTGIb',
  });

  return (
    <div>
      <WishlistsWelcomeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onButtonClick={closeModal}
        headerText={welcomeModalHeaderText}
        videoSrc={VIDEO_SRC}
        contentTitle={welcomeModalContentTitle}
        contentText={welcomeModalContentText}
        buttonLabel={welcomeModalButtonLabel}
        imageFallbackSrc={IMAGE_FALLBACK_SRC}
      />

      {isMobile && hasRegularWishlists && (
        <WishlistsActions
          editMode={editMode}
          setEditMode={setEditMode}
          showEditText
          hideActions={!isAuthenticated}
          userRole={EWishlistRoles.None}
          rentalCategory={rentalCategory}
        />
      )}

      <Heading
        className="pt-12 pb-6 md:pt-0 md:pb-10"
        variant={isMobile ? EHeadingStyleVariant.H3 : EHeadingStyleVariant.H4}>
        <FormattedMessage defaultMessage="Wishlists" id="C9pTM6" />
      </Heading>

      <div className="flex flex-col gap-8">
        {isAuthenticated ? (
          <>
            {isLoading && <WishlistsSkeleton />}

            {showWishlistGrid && (
              <WishlistGrid
                wishlists={regularWishlists}
                recentlyViewedTiles={recentlyViewedTiles}
                editMode={editMode}
                refetchWishlists={refetchWishlists}
                isMobile={isMobile}
                rentalCategory={rentalCategory}
              />
            )}

            {showEmptyWishlistMessage && (
              <EmptyWishlistMessage
                content={intl.formatMessage({
                  defaultMessage: 'Tap a heart icon to save it to your favorites.',
                  id: 'oG2fBd',
                })}
                buttonLabel={exploreRentalsBtnLabel}
                className="max-w-28"
                buttonHref="/rv-search"
              />
            )}

            {showInspiringWishlists && <InspiringWishlists wishlists={inspiringWishlists} />}
          </>
        ) : (
          <WishlistsAuthPrompt />
        )}
      </div>
    </div>
  );
};
